import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import axios from "axios";
import { ArrowLeft } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function formatDate(iso) {
  try {
    const d = new Date(iso);
    return d.toLocaleDateString("en-GB", { year: "numeric", month: "long", day: "numeric" });
  } catch {
    return iso;
  }
}

export default function InsightPost() {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [state, setState] = useState("loading");

  useEffect(() => {
    setState("loading");
    axios
      .get(`${API}/posts/${slug}`)
      .then((r) => {
        setPost(r.data);
        setState("ready");
      })
      .catch(() => setState("error"));
  }, [slug]);

  if (state === "loading") {
    return (
      <div className="mx-auto max-w-[820px] px-6 lg:px-10 py-32" data-testid="post-loading">
        <p className="text-bos-charcoal/60">Loading…</p>
      </div>
    );
  }

  if (state === "error" || !post) {
    return (
      <div className="mx-auto max-w-[820px] px-6 lg:px-10 py-32" data-testid="post-error">
        <p className="bos-eyebrow">Not found</p>
        <h1 className="mt-4 text-3xl font-medium text-bos-navy">That post is not in the studio.</h1>
        <Link to="/insights" className="mt-6 inline-flex items-center gap-2 text-bos-primary bos-link-underline">
          <ArrowLeft size={14} /> Back to Insights
        </Link>
      </div>
    );
  }

  return (
    <article className="mx-auto max-w-[820px] px-6 lg:px-10 pt-20 lg:pt-24 pb-24" data-testid="post-page">
      <Link to="/insights" data-testid="post-back" className="inline-flex items-center gap-2 text-bos-primary bos-link-underline text-[14px]">
        <ArrowLeft size={14} /> All insights
      </Link>
      <p className="bos-eyebrow mt-10">{post.category}</p>
      <h1 className="mt-5 text-4xl lg:text-[52px] font-medium text-bos-navy tracking-tight leading-[1.1]" data-testid="post-title">
        {post.title}
      </h1>
      <div className="mt-6 flex items-center gap-5 text-[13px] text-bos-charcoal/60">
        <span>{formatDate(post.published_at)}</span>
        <span>{post.read_minutes} min read</span>
      </div>
      <div className="mt-12 space-y-6 text-[17px] text-bos-charcoal/90 leading-[1.85]" data-testid="post-body">
        {post.body.split("\n\n").map((para, i) => (
          <p key={i}>{para}</p>
        ))}
      </div>

      <div className="mt-16 p-8 bg-bos-ice/60 border border-bos-light/40 rounded-sm" data-testid="post-cta">
        <p className="bos-eyebrow">Keep going</p>
        <h2 className="mt-3 text-2xl font-medium text-bos-navy">
          Want to apply this to your business?
        </h2>
        <p className="mt-3 text-[15px] text-bos-charcoal/80">
          Book a discovery call and we will spend the first hour listening, not selling.
        </p>
        <Link
          to="/booking"
          data-testid="post-cta-book"
          className="mt-6 inline-flex items-center bg-bos-navy text-bos-white px-6 py-3 rounded-full hover:bg-bos-primary transition-colors text-[14px]"
        >
          Book a discovery call
        </Link>
      </div>
    </article>
  );
}
